import React, { useContext } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_USER_GUIDE,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { alpha } from "@mui/material/styles";
import AppButton from "../common/AppButton";
import WelcomeParallax from "./WelcomeParallax";
import { WELCOME_FEATURES } from "./welcomeSectionContent";
import {
  welcomeFeatureCardSx,
  welcomeSectionContentSx,
  welcomeSectionIntroSx,
  welcomeSectionSx,
  welcomeSectionTitleSx
} from "./welcomeSectionStyles";

const WelcomeFeaturesSection: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext);
  const userGuidePath = localizedPath(
    PAGE_ROUTE_USER_GUIDE,
    language,
    defaultLang
  );

  const leftFeatures = WELCOME_FEATURES.slice(0, 2);
  const rightFeatures = WELCOME_FEATURES.slice(2, 4);

  const renderFeature = (feature: (typeof WELCOME_FEATURES)[number]) => {
    const Icon = feature.icon;
    return (
      <Box key={feature.id} sx={welcomeFeatureCardSx}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: "primary.main",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1.5,
            mx: { xs: "auto", md: 0 }
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Typography
          fontWeight={700}
          color="primary.main"
          sx={{ mb: 0.75, fontSize: "1rem" }}
        >
          {t(feature.titleKey, feature.titleFallback)}
        </Typography>
        <Typography
          color="text.secondary"
          lineHeight={1.7}
          fontSize="0.9375rem"
        >
          {t(feature.bodyKey, feature.bodyFallback)}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      component="section"
      data-testid="welcome-features-section"
      sx={welcomeSectionSx}
    >
      <Box sx={welcomeSectionContentSx}>
        <Typography
          component="h2"
          sx={{ ...welcomeSectionTitleSx, textAlign: "center" }}
        >
          {t("welcome-features-title", "Product features")}
        </Typography>
        <Typography
          sx={{
            ...welcomeSectionIntroSx,
            textAlign: "center",
            maxWidth: 640,
            mx: "auto"
          }}
        >
          {t(
            "welcome-features-intro",
            "Everything you need to run inventory, sales, and store operations—with clear reporting for owners and staff."
          )}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            gap: { xs: 4, md: 3 },
            mt: 4
          }}
        >
          <Box sx={{ flex: 1, width: "100%" }}>
            <WelcomeParallax offset={48} direction="reverse">
              <Stack
                spacing={3}
                sx={{
                  width: "100%",
                  textAlign: { xs: "center", md: "right" }
                }}
              >
                {leftFeatures.map(renderFeature)}
              </Stack>
            </WelcomeParallax>
          </Box>

          <Box
            sx={{
              flex: { xs: "none", md: "0 0 220px" },
              width: "100%",
              maxWidth: 220
            }}
          >
            <WelcomeParallax offset={64}>
              <Box
                sx={{
                  mx: "auto",
                  width: { xs: 180, md: 200 },
                  height: { xs: 320, md: 360 },
                  borderRadius: 4,
                  border: `8px solid ${alpha("#1a1a2e", 0.85)}`,
                  bgcolor: "#fff",
                  boxShadow: `0 24px 48px ${alpha("#6f49c9", 0.2)}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2
                }}
              >
                <Typography
                  fontWeight={700}
                  fontSize={{ xs: "0.9rem", md: "1rem" }}
                  color="primary.main"
                  textAlign="center"
                  lineHeight={1.3}
                >
                  {t("welcome-features-mock-brand", "Kivunova FMS")}
                </Typography>
              </Box>
            </WelcomeParallax>
          </Box>

          <Box sx={{ flex: 1, width: "100%" }}>
            <WelcomeParallax offset={48}>
              <Stack
                spacing={3}
                sx={{
                  width: "100%",
                  textAlign: { xs: "center", md: "left" }
                }}
              >
                {rightFeatures.map(renderFeature)}
              </Stack>
            </WelcomeParallax>
          </Box>
        </Box>

        <Box sx={{ textAlign: "center", mt: 5 }}>
          <AppButton
            component={Link}
            to={userGuidePath}
            variant="primary"
            endIcon={<ArrowForwardIcon />}
            data-testid="welcome-features-guide-link"
          >
            {t("welcome-features-cta", "Explore the user guide")}
          </AppButton>
        </Box>
      </Box>
    </Box>
  );
};

export default WelcomeFeaturesSection;
